import { sqItemRuntimeStore, sqTrendScalingColumnStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { findItemIn, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { recordDataStatus } from '@/trendData/itemRuntime.utilities';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import type { PushOption } from '@/core/flux.service';
import type { Item } from '@/trendData/itemStore.utilities';
import type { CustomizationPayload, DataStatusPayload } from '@/trendData/itemStoreBase.types';

/**
 * Explicit callers for base item events whose target store is NOT statically knowable; an
 * arbitrary item id or a mixed collection. When the affected store IS known, use the
 * store-targeted setter in that store's actions file instead (e.g. `setSeriesDataStatusLoading`).
 *
 * Specifically a function to work around import ordering issues
 */
function getConvertedTrendItemStores() {
  return [sqTrendScalingColumnStore];
}

export function removeItemsFromTrendStores(
  payload: { items: Array<Pick<Item, 'id'>>; numPixels?: number },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.removeItems(payload));
  flux.dispatch('TREND_REMOVE_ITEMS', payload, ...option);
}

export function swapItemsInTrendStores(payload: { swaps: Record<string, string> }, ...option: [PushOption?]): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.swapItems(payload));
  flux.dispatch('TREND_SWAP_ITEMS', payload, ...option);
}

export function setSelectedInTrendStores(
  payload: { item: Item; selected: boolean; numPixels?: number },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setSelected(payload));
  flux.dispatch('TREND_SET_SELECTED', payload, ...option);
}

export function setSelectedCapsulesInTrendStores(
  payload: { capsules: Array<Pick<Item, 'id'>>; selected: boolean; numPixels?: number },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setSelectedCapsules(payload));
  flux.dispatch('TREND_SET_SELECTED_CAPSULES', payload, ...option);
}

export function setColorInTrendStores(
  payload: { id: string; color: string | undefined },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setColor(payload));
  flux.dispatch('TREND_SET_COLOR', payload, ...option);
}

export function setRequestProgressInTrendStores(
  payload: { id: string; progress?: number | string; timingInformation?: string; meterInformation?: string },
  ...option: [PushOption?]
): void {
  if (findItemIn(getTrendStores(), payload.id)) {
    sqItemRuntimeStore.actions.setRequestProgress(payload.id, payload);
  }
  flux.dispatch('REQUEST_SET_PROGRESS', payload, ...option);
}

export function setPropertiesInTrendStores(
  payload: { id: string; includeChildren?: boolean } & Record<string, unknown>,
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setProperties(payload));
  flux.dispatch('TREND_SET_PROPERTIES', payload, ...option);
}

export function setCustomizationsInTrendStores(
  payload: { items: CustomizationPayload[] },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setCustomizations(payload));
  flux.dispatch('TREND_SET_CUSTOMIZATIONS', payload, ...option);
}

export function setDataStatusLoadingInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.LOADING, { id });
}

export function setDataStatusPresentInTrendStores(payload: DataStatusPayload): void {
  recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
}

export function setDataStatusNotRequiredInTrendStores(id: string, ...option: [PushOption?]): void {
  const payload = { id };
  recordDataStatus(ITEM_DATA_STATUS.NOT_REQUIRED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  flux.dispatch('TREND_SET_DATA_STATUS_NOT_REQUIRED', payload, ...option);
}

export function setDataStatusCanceledInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.CANCELED, { id });
}

export function setDataStatusCanceledRetriesInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.CANCELED_RETRIES, { id });
}

export function setDataStatusFailureInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.FAILURE, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  flux.dispatch('TREND_SET_DATA_STATUS_FAILURE', payload, ...option);
}

export function setDataStatusRedactedInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.REDACTED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  flux.dispatch('TREND_SET_DATA_STATUS_REDACTED', payload, ...option);
}

export function setDataStatusAbortedInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.ABORTED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  flux.dispatch('TREND_SET_DATA_STATUS_ABORTED', payload, ...option);
}
